import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Component, Input, Inject } from '@angular/core';
import { FormGroup, FormArray, UntypedFormBuilder, ValidatorFn, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { UserAdminService } from '@Services';
import UserUnavailabilitySchedule from '@Models/global/user-unavailability-schedule';
import { isMoment } from 'moment';
import {
  NgxMatDateFormats,
  NGX_MAT_DATE_FORMATS
} from '@angular-material-components/datetime-picker';

const CUSTOM_DATE_FORMATS: NgxMatDateFormats = {
  parse: {
    dateInput: 'MM/DD/YY hh:mm A',
  },
  display: {
    dateInput: "MM/DD/YY hh:mm A [CST]",
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: "MM/DD/YY hh:mm A [CST]",
    monthYearA11yLabel: 'MMMM YYYY',
  }
};

@Component({
    selector: 'unavailability-schedule',
    templateUrl: './unavailability-schedule.component.html',
    styleUrls: ['./unavailability-schedule.component.scss'],
    providers: [{ provide: NGX_MAT_DATE_FORMATS, useValue: CUSTOM_DATE_FORMATS }]

})

export class UnavailabilityScheduleComponent {
    @Input() currentUser: any;
    @Input() userAvailable: any;
    
    userStatus: string;

    scheduleForm!: FormGroup;
    today: Date;
    userSchedule: UserUnavailabilitySchedule[] = [];

    constructor(
        private readonly userAdminService: UserAdminService,
        private _fb: UntypedFormBuilder,
        public scheduleDialogRef: MatDialogRef<UnavailabilityScheduleComponent>,
        @Inject(MAT_DIALOG_DATA) public data: any
    ) { }
    //#region private helpers

    loadUnavailabilitySchedule() {
        this.userAdminService.getUserUnavailabilitySchedule(this.data.currentUser.id, true).subscribe((response) => {
            if (response) {
                this.userSchedule = response;
                console.log('UnavailabilitySchedule initialized with data:', this.userSchedule);
                this.userSchedule.forEach(s => this.addSchedule(s));
            }
        });
    }

    get schedules(): FormArray {
        return this.scheduleForm.get('schedules') as FormArray;
    }
    
    addSchedule(schedule: any = null): void {
        const group = this._fb.group({
            id: [schedule?.id || null],
            userId: [schedule?.userId || this.data.currentUser.id, Validators.required],
            startTime: [schedule?.formattedStartTime || null, Validators.required],
            endTime: [schedule?.formattedEndTime || null, Validators.required],
            isActive: [schedule?.isActive || true, Validators.required]
        }, {
        validators: [
            startTimeNotInPastIfIdNullValidator('startTime', 'id'),
            endTimeAfterStartTimeValidator('startTime', 'endTime'),
        ]
        });

        if (schedule) {
            group['originalValue'] = JSON.parse(JSON.stringify(group.value));  
        }

        this.schedules.push(group);
    }

    getUpdatedSchedules(): any[] {
        const updatedSchedules = [];

        this.schedules.controls.forEach((group: FormGroup) => {
            const originalValue = group['originalValue'];  
            const currentValue = group.value;             

            if (JSON.stringify(originalValue) !== JSON.stringify(currentValue)) {
            updatedSchedules.push(group.value);  
            }
        });

        return updatedSchedules;
    }

    removeSchedule(index: number): void {
        const scheduleControl = this.schedules.at(index);

        if (scheduleControl.get('id')?.value) {
            scheduleControl.patchValue({ isActive: false });
        } else {
            this.schedules.removeAt(index);
        }
    }

    saveForm(): void {
        const updatedSchedules = this.getUpdatedSchedules();
  
        const payload = updatedSchedules.map((schedule: any) => {
            const start = schedule.startTime;
            const end = schedule.endTime;

            if (isMoment(start)) {
                schedule.startTime = start.toDate().toLocaleString();
            }

            if (isMoment(end)) {
                schedule.endTime = end.toDate().toLocaleString();
            }

            return schedule;
        });

        if (payload.length > 0) {
            this.userAdminService.upsertUserUnavailabilitySchedule(this.data.currentUser.id, payload).subscribe((response) => {
            });
        }
    }

    getMinimum(group: AbstractControl): Date {
        if (!group.get('id')?.value) {
            return this.today;
        }
        else {
            const startTime = group.get('startTime')?.value;
            return startTime ? new Date(startTime) : this.today;
        }
    }

    cancel() {
        this.scheduleDialogRef.close();
    }

    saveAndClose() {
        this.saveForm();
        this.scheduleDialogRef.close();
    }

    //#endregion

    //#region Lifecycle Hooks

    ngOnInit(): void {
        this.scheduleForm = this._fb.group({
            schedules: this._fb.array([])
        });

        this.userStatus = this.data.userAvailable ? 'Available' : 'Unavailable';
        const now = new Date();
        this.today = new Date(now.getFullYear(), now.getMonth(), now.getDate(), now.getHours(), now.getMinutes(), now.getSeconds());
        
        this.loadUnavailabilitySchedule();
    }

    ngOnDestroy() {
    }

    //#endregion
}


export function endTimeAfterStartTimeValidator(startKey: string, endKey: string): ValidatorFn {
  return (group: AbstractControl): { [key: string]: any } | null => {
    const start = group.get(startKey)?.value;
    const end = group.get(endKey)?.value;

    if (!start || !end) return null;

    if (new Date(end) < new Date(start)) {
      group.get(endKey)?.setErrors({ endBeforeStart: true });
      return { endBeforeStart: true };
    } else {
      const endControl = group.get(endKey);
      if (endControl?.hasError('endBeforeStart')) {
        const errors = { ...endControl.errors };
        delete errors['endBeforeStart'];
        endControl.setErrors(Object.keys(errors).length ? errors : null);
      }
    }

    return null;
  };
}

export function startTimeNotInPastIfIdNullValidator(startKey: string, idKey: string): ValidatorFn {
  return (group: AbstractControl): { [key: string]: any } | null => {
    const start = group.get(startKey)?.value;
    const id = group.get(idKey)?.value;

    if (!start || id) return null;

    const now = new Date();
    const selectedStart = new Date(start);
    const startControl = group.get(startKey);

    if (selectedStart < now) {
      startControl?.setErrors({ pastDateTime: true });
      return { pastDateTime: true };
    } else {
      if (startControl?.hasError('pastDateTime')) {
        const errors = { ...startControl.errors };
        delete errors['pastDateTime'];
        startControl.setErrors(Object.keys(errors).length ? errors : null);
      }
    }

    return null;
  };
}